"use client";

const DEFAULT_TITLE_WAIT_TIMEOUT_MS = 500;

function scheduleSoon(callback: () => void): number {
  if (
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function"
  ) {
    return window.requestAnimationFrame(() => callback());
  }
  return window.setTimeout(callback, 0);
}

function cancelScheduled(handle: number | null): void {
  if (handle == null || typeof window === "undefined") return;
  if (typeof window.cancelAnimationFrame === "function") {
    window.cancelAnimationFrame(handle);
    return;
  }
  window.clearTimeout(handle);
}

type WaitForDocumentTitleOptions = {
  previousTitle?: string | null;
  signal?: AbortSignal;
  timeoutMs?: number;
};

export async function waitForDocumentTitle({
  previousTitle = null,
  signal,
  timeoutMs = DEFAULT_TITLE_WAIT_TIMEOUT_MS,
}: WaitForDocumentTitleOptions = {}): Promise<void> {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  if (signal?.aborted) return;

  const normalizedPreviousTitle = previousTitle?.trim() ?? "";
  const hasUsableTitle = () => {
    const currentTitle = document.title.trim();
    if (!currentTitle) return false;
    if (!normalizedPreviousTitle) return true;
    return currentTitle !== normalizedPreviousTitle;
  };

  if (hasUsableTitle()) return;

  await new Promise<void>((resolve) => {
    let finished = false;
    let observer: MutationObserver | null = null;
    let timeoutHandle: number | null = null;
    let scheduledHandle: number | null = null;

    const cleanup = () => {
      observer?.disconnect();
      observer = null;
      if (timeoutHandle != null) {
        window.clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
      cancelScheduled(scheduledHandle);
      scheduledHandle = null;
      signal?.removeEventListener("abort", finish);
    };

    function finish() {
      if (finished) return;
      finished = true;
      cleanup();
      resolve();
    }

    const maybeFinish = () => {
      if (!hasUsableTitle() || scheduledHandle != null) return;
      scheduledHandle = scheduleSoon(finish);
    };

    if (typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(maybeFinish);
      observer.observe(document.head, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    timeoutHandle = window.setTimeout(finish, timeoutMs);
    signal?.addEventListener("abort", finish, { once: true });
    maybeFinish();
  });
}
